import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, ArrowUpCircle, Check, X, Clock } from "lucide-react";
import api from "../../lib/api";
import { PageHeader, Table, Td } from "../../components/dashboard/Shared";
import SubscriptionCard from "../../components/SubscriptionCard";
import ContactSupportModal from "../../components/ContactSupportModal";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso.slice(0, 16); }
};

const ACTION_LABEL = {
  change_plan: "Plan Changed",
  status_change: "Status Change",
  renew: "Renewed",
  set_expiry: "Expiry Updated",
  set_price: "Price Updated",
  grant_lifetime: "Lifetime Granted",
  mt5_override: "MT5 Override",
  grandfathered: "Grandfathered",
};

export default function Billing() {
  const [history, setHistory] = useState([]);
  const [plans, setPlans] = useState([]);
  const [supportOpen, setSupportOpen] = useState(false);
  const [desiredPlan, setDesiredPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/subscription/history").catch(() => ({ data: { history: [] } })),
      api.get("/subscription/plans").catch(() => ({ data: { plans: [] } })),
    ]).then(([h, p]) => {
      setHistory(h.data.history || []);
      setPlans(p.data.plans || []);
      setLoading(false);
    });
  }, []);

  const askUpgrade = (plan) => { setDesiredPlan(plan); setSupportOpen(true); };

  return (
    <div className="space-y-6">
      <PageHeader title="Subscription" sub="Manage your ChartzFx plan and view your subscription history" />

      <SubscriptionCard onOpenSupport={() => { setDesiredPlan(null); setSupportOpen(true); }} />

      {/* Upgrade options */}
      <div className="card-dark p-6" data-testid="upgrade-options">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white text-lg">Available Plans</h3>
            <p className="text-xs text-gray-500 mt-1">Choose a plan and contact support to upgrade or activate.</p>
          </div>
          <ArrowUpCircle className="w-5 h-5 text-[#7000FF]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {plans.map((p) => (
            <div key={p.code} className={`border rounded-lg p-4 hover:border-[#7000FF]/50 transition-colors cursor-pointer ${p.highlighted ? "border-[#7000FF]/40 bg-[#7000FF]/5" : "border-white/10"}`}
                 onClick={() => askUpgrade(p.code)}
                 data-testid={`upgrade-plan-${p.code.toLowerCase()}`}>
              <p className="text-xs text-gray-400 uppercase tracking-wider">{p.name}</p>
              <p className="text-lg text-white mt-1 tabular">{p.price_display}{p.period ? <span className="text-xs text-gray-500">/{p.period}</span> : null}</p>
              <p className="text-xs text-gray-500 mt-2">
                {p.mt5_limit === -1 ? "Unlimited MT5" : `Up to ${p.mt5_limit} MT5 ${p.mt5_limit === 1 ? "Account" : "Accounts"}`}
              </p>
              <button className="btn-outline w-full mt-3 py-1.5 text-xs">
                {p.cta}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Subscription history */}
      <div className="card-dark p-6" data-testid="subscription-history">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-4 h-4 text-[#7000FF]" />
          <h3 className="text-white text-lg">Subscription History</h3>
        </div>
        {loading ? (
          <div className="skeleton h-24" />
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">No subscription changes yet.</p>
        ) : (
          <Table headers={["Date", "Action", "Details", "By"]}>
            {history.slice().reverse().map((h, i) => (
              <tr key={i} className="border-b border-white/5">
                <Td>{fmtDate(h.at)}</Td>
                <Td className="text-[#00F0FF]">{ACTION_LABEL[h.action] || h.action}</Td>
                <Td>
                  {h.to_plan && <>Plan: <span className="text-white">{h.to_plan}</span>{h.from_plan ? ` (was ${h.from_plan})` : ""}</>}
                  {h.to_status && <>Status: <span className="text-white">{h.to_status}</span></>}
                  {h.days && <>+{h.days} days</>}
                  {h.plan && !h.to_plan && <>Plan: <span className="text-white">{h.plan}</span></>}
                  {h.override !== undefined && <>Override: <span className="text-white">{h.override ?? "cleared"}</span></>}
                  {h.note && <p className="text-xs text-gray-500 mt-1">{h.note}</p>}
                </Td>
                <Td className="text-xs text-gray-500">{h.changed_by ? h.changed_by.slice(0, 10) : "system"}</Td>
              </tr>
            ))}
          </Table>
        )}
      </div>

      <ContactSupportModal open={supportOpen}
                            onClose={() => setSupportOpen(false)}
                            desiredPlan={desiredPlan} />
    </div>
  );
}
